// apps/client/src/store/userStore.ts
//
// User Store - Extended state for missions, referrals, economy, onboarding, settings
//

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Howler } from 'howler';
import { api, Mission, ReferralStats } from '../lib/api';

// Wallet cap per PDR
const WALLET_CAP = 50_000;

interface UserSettings {
    sound: boolean;
    vibration: boolean;
}

interface UserStats {
    bingosWon: number;
    gamesPlayed: number;
    totalEarned: number;
}

interface UserState {
    // Economy
    chips: number;

    // Onboarding / Legal (persisted)
    hasCompletedOnboarding: boolean;
    hasAcceptedLegal: boolean;
    hasClaimedWelcomeBonus: boolean;

    // Settings (persisted)
    settings: UserSettings;

    // Streaks (persisted)
    currentStreak: number;
    lastLoginDate: string | null;

    // User Stats
    userStats: UserStats;

    // Missions
    missions: Mission[];
    missionsLoading: boolean;
    missionsError: string | null;

    // Referrals
    referralCode: string | null;
    referralShareUrl: string | null;
    referralStats: ReferralStats | null;
    referralLoading: boolean;

    // Ads
    adWatchingPlacement: string | null;
    lastAdWatched: Date | null;

    // Server settings (fetched from /settings/public)
    welcomeBonusAmount: number;

    // Actions - Onboarding
    completeOnboarding: () => Promise<void> | void;
    acceptLegal: () => Promise<void> | void;
    claimWelcomeBonus: () => Promise<void>;
    fetchPublicSettings: () => Promise<void>;
    updateSettings: (partial: Partial<UserSettings>) => void;

    // Actions - Missions
    fetchMissions: () => Promise<void>;
    claimReward: (missionId: string) => Promise<{ success: boolean; reward?: number }>;

    // Actions - Referrals
    fetchReferralData: () => Promise<void>;
    applyReferralCode: (code: string) => Promise<{ success: boolean; message: string }>;

    // Actions - Ads
    watchAd: (placement?: import('../lib/adsgram').AdPlacement) => Promise<{ success: boolean; reward?: number; newBalance?: number; adToken?: string | undefined }>;

    // Actions - Economy
    refreshBalance: () => Promise<void>;
    updateChips: (chips: number) => void;
    addChips: (amount: number) => void;

    // Actions - Streaks
    checkDailyStreak: () => void;
}

export const useUserStore = create<UserState>()(
    persist(
        (set, get) => ({
            // Initial state
            chips: 0,
            hasCompletedOnboarding: false,
            hasAcceptedLegal: false,
            hasClaimedWelcomeBonus: false,
            settings: { sound: true, vibration: true },
            currentStreak: 0,
            lastLoginDate: null,
            userStats: { bingosWon: 0, gamesPlayed: 0, totalEarned: 0 },
            missions: [],
            missionsLoading: false,
            missionsError: null,
            referralCode: null,
            referralShareUrl: null,
            referralStats: null,
            referralLoading: false,
            adWatchingPlacement: null,
            lastAdWatched: null,
            welcomeBonusAmount: 500, // default, synced by fetchPublicSettings

            // ============ ONBOARDING ============
            completeOnboarding: async () => {
                try {
                    await api.completeOnboarding();
                } catch { /* best-effort */ }
                set({ hasCompletedOnboarding: true });
            },
            acceptLegal: async () => {
                set({ hasAcceptedLegal: true });
                try {
                    await api.acceptLegal();
                } catch { /* best-effort — local state already set */ }
            },

            claimWelcomeBonus: async () => {
                try {
                    const res = await api.claimWelcomeBonus();
                    if (res.ok) {
                        set({
                            hasClaimedWelcomeBonus: true,
                            chips: Math.min(WALLET_CAP, res.newBalance),
                            welcomeBonusAmount: res.reward,
                        });
                    }
                } catch (err) {
                    // Do NOT fall back to local chips — only server-confirmed bonuses count
                    console.error('Welcome bonus claim failed:', err);
                }
            },

            fetchPublicSettings: async () => {
                try {
                    const res = await api.getPublicSettings();
                    if (res.data?.welcomeBonus) {
                        set({ welcomeBonusAmount: res.data.welcomeBonus });
                    }
                } catch {
                    // silent — keep defaults
                }
            },

            updateSettings: (partial) => {
                set((state) => ({
                    settings: { ...state.settings, ...partial },
                }));
            },

            // ============ STREAKS ============
            checkDailyStreak: () => {
                const now = new Date();
                const today = now.toISOString().split('T')[0]!; // YYYY-MM-DD

                const { lastLoginDate, currentStreak } = get();

                if (!lastLoginDate) {
                    set({ lastLoginDate: today, currentStreak: 1 });
                    return;
                }

                if (lastLoginDate === today) {
                    // Already logged in today
                    return;
                }

                // Check if yesterday
                const yesterdayDate = new Date(now);
                yesterdayDate.setDate(yesterdayDate.getDate() - 1);
                const yesterday = yesterdayDate.toISOString().split('T')[0]!;

                if (lastLoginDate === yesterday) {
                    // Logged in yesterday, increment streak
                    set({ lastLoginDate: today, currentStreak: currentStreak + 1 });
                } else {
                    // Streak broken (missed at least one day)
                    set({ lastLoginDate: today, currentStreak: 1 });
                }
            },

            // ============ MISSIONS ============
            fetchMissions: async () => {
                set({ missionsLoading: true, missionsError: null });
                try {
                    const response = await api.getMissions();

                    if (response.ok) {
                        set({ missions: response.missions });
                        console.log(`✅ Misiones cargadas: ${response.missions.length}`);
                    }
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to fetch missions';
                    set({ missionsError: message });
                    console.error('❌ Error cargando misiones:', message);
                } finally {
                    set({ missionsLoading: false });
                }
            },

            claimReward: async (missionId: string) => {
                try {
                    const response = await api.claimMission(missionId);

                    if (response.ok) {
                        // Cap the balance
                        const newBalance = Math.min(WALLET_CAP, response.newBalance);
                        set({ chips: newBalance });

                        // Update mission status in local state
                        set(state => ({
                            missions: state.missions.map(m =>
                                m.id === missionId
                                    ? { ...m, status: 'CLAIMED' as const, canClaim: false }
                                    : m
                            ),
                        }));

                        console.log(`🎁 Recompensa reclamada: ${response.reward} chips -> Nuevo saldo: ${newBalance}`);
                        return { success: true, reward: response.reward };
                    }

                    return { success: false };
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to claim';
                    console.error('❌ Error reclamando recompensa:', message);
                    return { success: false };
                }
            },

            // ============ REFERRALS ============
            fetchReferralData: async () => {
                set({ referralLoading: true });
                try {
                    const [codeResponse, statsResponse] = await Promise.all([
                        api.getReferralCode(),
                        api.getReferralStats(),
                    ]);

                    if (codeResponse.ok) {
                        set({
                            referralCode: codeResponse.code,
                            referralShareUrl: codeResponse.shareUrl,
                        });
                    }

                    if (statsResponse.ok) {
                        set({ referralStats: statsResponse.stats });
                    }
                } catch (error) {
                    console.error('❌ Error cargando datos de referidos:', error);
                } finally {
                    set({ referralLoading: false });
                }
            },

            applyReferralCode: async (code: string) => {
                try {
                    const response = await api.applyReferralCode(code);

                    if (response.ok) {
                        return { success: true, message: response.message };
                    }

                    return { success: false, message: 'Failed to apply code' };
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Invalid code';
                    return { success: false, message };
                }
            },

            // ============ ADS ============
            watchAd: async (placement = 'store_chips' as import('../lib/adsgram').AdPlacement) => {
                set({ adWatchingPlacement: placement });

                const { settings } = get();
                const wasSoundEnabled = settings.sound;
                Howler.mute(true);

                try {
                    console.log(`📺 Starting ad (placement: ${placement})...`);

                    let adCompleted = false;
                    let provider: 'adsgram' | 'monetag' = 'monetag';

                    // Request C2S verification token BEFORE showing ad
                    let adToken: string | undefined;
                    try {
                        const tokenRes = await api.getAdToken(placement);
                        if (tokenRes.ok) adToken = tokenRes.adToken;
                    } catch (e) {
                        console.warn('⚠️ Failed to get ad token:', e);
                    }

                    // iOS: Skip Monetag — it redirects to Safari, which closes the TMA.
                    // Only use Adsgram on iOS (renders inline in the WebView).
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

                    if (isIOS) {
                        // iOS-only: Adsgram only (no Monetag)
                        try {
                            const { showAd, isAdsgramReady } = await import('../lib/adsgram');
                            if (isAdsgramReady()) {
                                console.log('🎬 Adsgram (iOS-only): showing ad...');
                                adCompleted = await showAd(placement);
                                if (adCompleted) provider = 'adsgram';
                            }
                        } catch (e) {
                            console.log('⚠️ Adsgram failed on iOS:', e);
                        }
                    } else {
                        // Determine which provider to try first (alternate)
                        const lastProvider = localStorage.getItem('lastAdProvider') || 'adsgram';
                        const tryMonetagFirst = lastProvider === 'adsgram';

                        if (tryMonetagFirst) {
                            // Try Monetag first
                            try {
                                const { showMonetag, isMonetagReady } = await import('../lib/monetag');
                                if (isMonetagReady()) {
                                    console.log('🎬 Monetag (primary): showing ad...');
                                    adCompleted = await showMonetag();
                                    if (adCompleted) provider = 'monetag';
                                }
                            } catch (e) {
                                console.log('⚠️ Monetag failed:', e);
                            }

                            // Fallback to Adsgram
                            if (!adCompleted) {
                                try {
                                    const { showAd, isAdsgramReady } = await import('../lib/adsgram');
                                    if (isAdsgramReady()) {
                                        console.log('🎬 Adsgram (fallback): showing ad...');
                                        adCompleted = await showAd(placement);
                                        if (adCompleted) provider = 'adsgram';
                                    }
                                } catch (e) {
                                    console.log('⚠️ Adsgram fallback failed:', e);
                                }
                            }
                        } else {
                            // Try Adsgram first
                            try {
                                const { showAd, isAdsgramReady } = await import('../lib/adsgram');
                                if (isAdsgramReady()) {
                                    console.log('🎬 Adsgram (primary): showing ad...');
                                    adCompleted = await showAd(placement);
                                    if (adCompleted) provider = 'adsgram';
                                }
                            } catch (e) {
                                console.log('⚠️ Adsgram failed:', e);
                            }

                            // Fallback to Monetag
                            if (!adCompleted) {
                                try {
                                    const { showMonetag, isMonetagReady } = await import('../lib/monetag');
                                    if (isMonetagReady()) {
                                        console.log('🎬 Monetag (fallback): showing ad...');
                                        adCompleted = await showMonetag();
                                        if (adCompleted) provider = 'monetag';
                                    }
                                } catch (e) {
                                    console.log('⚠️ Monetag fallback failed:', e);
                                }
                            }
                        }
                    } // end !isIOS

                    // Track which provider was used for next rotation
                    if (adCompleted) {
                        localStorage.setItem('lastAdProvider', provider);
                        console.log(`✅ Ad served by: ${provider} (next will try ${provider === 'monetag' ? 'adsgram' : 'monetag'})`);
                    }

                    // If no ad SDK loaded, do NOT allow a free claim
                    if (!adCompleted) {
                        console.log('⚠️ No ad SDK available — cannot claim without watching.');
                        return { success: false };
                    }

                    if (!adCompleted) {
                        return { success: false };
                    }

                    // Card-earning placements don't need chip claim — track server-side for analytics + bundle validation
                    const customHandlePlacements = ['game_single', 'game_bundle', 'fortune_wheel_spin'];
                    if (customHandlePlacements.includes(placement)) {
                        console.log('✅ Ad completed! Custom handle placement — tracking server-side.');
                        // Track ad view server-side (non-blocking) for bundle validation
                        api.trackAdView(placement).catch(() => { /* non-critical */ });
                        // Fire mission progress (non-blocking)
                        api.triggerAdWatched().catch(() => { /* non-critical */ });
                        get().fetchMissions();
                        return { success: true, reward: 0, adToken };
                    }

                    // Ad completed → claim chip reward from server
                    console.log('✅ Ad completed! Claiming chip reward...');
                    const previousChips = get().chips;

                    try {
                        const claimResponse = await api.claimAdReward(placement, adToken);

                        if (claimResponse.ok && claimResponse.reward > 0) {
                            const cappedBalance = Math.min(WALLET_CAP, claimResponse.newBalance);
                            const reward = claimResponse.reward;

                            set({
                                chips: cappedBalance,
                                lastAdWatched: new Date(),
                            });

                            console.log(`💰 Reward credited: +${reward} chips (${previousChips} → ${cappedBalance})`);

                            // Fire mission progress (non-blocking)
                            api.triggerAdWatched().catch(() => { /* non-critical */ });
                            get().fetchMissions();

                            return { success: true, reward, newBalance: cappedBalance };
                        }

                        // Server rejected claim or zero reward
                        if (claimResponse.ok) {
                            // Claim was accepted but reward was 0 — still successful ad view
                            api.triggerAdWatched().catch(() => { /* non-critical */ });
                            return { success: true, reward: 0 };
                        }

                        console.warn('⚠️ Claim rejected:', claimResponse);
                        return { success: false };
                    } catch (claimErr: any) {
                        console.error('❌ Ad claim API error:', claimErr?.message || claimErr);
                        return { success: false };
                    }
                } catch (error) {
                    console.error('❌ Ad error:', error);
                    return { success: false };
                } finally {
                    set({ adWatchingPlacement: null });
                    // Only unmute if User Settings has sound ON implicitly
                    if (wasSoundEnabled) {
                        Howler.mute(false);
                    }
                }
            },

            // ============ ECONOMY ============
            refreshBalance: async () => {
                // Retry up to 3 times (API may not be warm yet)
                for (let attempt = 1; attempt <= 3; attempt++) {
                    try {
                        const response = await api.getMe();
                        if (response.ok) {
                            set({
                                chips: Math.min(WALLET_CAP, response.user.chips),
                                hasCompletedOnboarding: response.user.hasCompletedOnboarding ?? get().hasCompletedOnboarding,
                                hasAcceptedLegal: response.user.hasAcceptedLegal ?? get().hasAcceptedLegal,
                                hasClaimedWelcomeBonus: response.user.hasClaimedWelcomeBonus ?? get().hasClaimedWelcomeBonus,
                            });
                            return; // success
                        }
                    } catch (error) {
                        console.warn(`⚠️ refreshBalance attempt ${attempt}/3 failed:`, error);
                        if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt));
                    }
                }
            },

            updateChips: (chips: number) => {
                set({ chips: Math.min(WALLET_CAP, chips) });
            },

            addChips: (amount: number) => {
                set((state) => ({
                    chips: Math.min(WALLET_CAP, state.chips + amount),
                }));
            },
        }),
        {
            name: 'bingoton-user-store',
            // Only persist these fields
            partialize: (state) => ({
                hasCompletedOnboarding: state.hasCompletedOnboarding,
                hasAcceptedLegal: state.hasAcceptedLegal,
                hasClaimedWelcomeBonus: state.hasClaimedWelcomeBonus,
                welcomeBonusAmount: state.welcomeBonusAmount,
                settings: state.settings,
                currentStreak: state.currentStreak,
                lastLoginDate: state.lastLoginDate,
                chips: state.chips, // Cache chips locally (server is source of truth)
            }),
        }
    )
);

// ============ REFERRAL STARTUP LOGIC ============
export async function checkAndApplyReferralFromUrl(): Promise<void> {
    try {
        const tg = window.Telegram?.WebApp;
        const startParam = (tg?.initDataUnsafe as { start_param?: string } | undefined)?.start_param;

        const urlParams = new URLSearchParams(window.location.search);
        const codeFromUrl = urlParams.get('ref') || urlParams.get('startapp');

        const referralCode = startParam || codeFromUrl;

        if (referralCode && referralCode.startsWith('BT-')) {
            console.log(`🔗 Código de referido detectado: ${referralCode}`);

            const result = await useUserStore.getState().applyReferralCode(referralCode);

            if (result.success) {
                console.log('✅ Referido aplicado correctamente');
            } else {
                console.log(`⚠️ No se pudo aplicar el referido: ${result.message}`);
            }
        }
    } catch (error) {
        console.error('Error checking referral code:', error);
    }
}
